/**
 * @fileoverview Inline assignee control (MOR-696)
 *
 * A compact avatar + name trigger that opens a searchable member list to
 * reassign a task without leaving the calendar surface. Used on the calendar
 * agenda/list rows so practitioners can change ownership at a glance. Resolves
 * the firm's members live (Convex dedupes the shared org query across rows) and
 * writes through `updateTask`, which materialises derived statutory rows on
 * first touch and records change history.
 *
 * Renders a read-only avatar + name when the row can't be targeted (no
 * persisted id and no statutory code to materialise from).
 */

import { useState } from "react";
import { useMutation, useQuery } from "convex/react";
import { api } from "@cvx/_generated/api";
import type { Id } from "@cvx/_generated/dataModel";
import { toast } from "sonner";
import { Check, UserPlus } from "lucide-react";

import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/ui/composed/menus/command";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/ui/composed/overlays/popover";
import { UserAvatar, getDisplayName } from "@/ui/composed";
import { cn } from "@/utils/misc";
import { extractErrorMessage } from "@/utils/errors";

/** Either an existing persisted id, or a (jobId, code) pair to materialise. */
type TaskTarget =
  { id: Id<"jobKeyDates"> } | { jobId: Id<"jobs">; code: string } | null;

export function AssigneeControl({
  orgId,
  jobId,
  persistedId,
  code,
  assigneeUserId,
  className,
}: {
  orgId: string;
  jobId: string;
  persistedId: string | null;
  code: string | null;
  assigneeUserId: string | null;
  className?: string;
}) {
  const [open, setOpen] = useState(false);
  const members = useQuery(api.organizations.queries.getOrganizationMembers, {
    orgId: orgId as Id<"organizations">,
  });
  const updateTask = useMutation(api.tasks.mutations.updateTask);

  const target: TaskTarget = persistedId
    ? { id: persistedId as Id<"jobKeyDates"> }
    : code
      ? { jobId: jobId as Id<"jobs">, code }
      : null;

  const current = (members ?? []).find(
    (m) => m.user._id === assigneeUserId,
  )?.user;

  const label = current
    ? getDisplayName(current.firstName, current.lastName, current.email)
    : "Unassigned";

  const reassign = async (next: string | null) => {
    setOpen(false);
    if (!target) return;
    try {
      await updateTask({
        ...target,
        patch: { assigneeUserId: next as Id<"users"> | null },
      } as Parameters<typeof updateTask>[0]);
    } catch (e) {
      toast.error(extractErrorMessage(e, "Could not update assignee."));
    }
  };

  // Shared trigger inner content so the read-only and editable variants match.
  const inner = (
    <>
      {current ? (
        <UserAvatar
          avatarUrl={current.avatarUrl}
          firstName={current.firstName}
          lastName={current.lastName}
          email={current.email}
          className="size-5 shrink-0 text-[9px]"
        />
      ) : (
        <span className="inline-flex size-5 shrink-0 items-center justify-center rounded-full border border-dashed border-foreground/30 text-foreground/50">
          <UserPlus className="size-3" />
        </span>
      )}
      <span className="max-w-[140px] truncate">{label}</span>
    </>
  );

  // No target → render a static chip (statutory rows that can't be touched).
  if (!target) {
    return (
      <span
        className={cn(
          "inline-flex items-center gap-1.5 rounded-md border border-foreground/15 bg-background px-1.5 py-0.5 text-[11px] font-medium text-foreground shadow-sm",
          className,
        )}
        title={label}
      >
        {inner}
      </span>
    );
  }

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          // The row is clickable to open the side panel; keep the popover's
          // own clicks from bubbling up and opening it.
          onClick={(e) => e.stopPropagation()}
          title={`Assignee: ${label}`}
          className={cn(
            "inline-flex items-center gap-1.5 rounded-md border border-foreground/15 bg-background px-1.5 py-0.5 text-[11px] font-medium text-foreground shadow-sm",
            "transition-colors hover:bg-muted focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring",
            className,
          )}
        >
          {inner}
        </button>
      </PopoverTrigger>
      <PopoverContent
        className="w-[240px] p-0"
        align="end"
        onClick={(e) => e.stopPropagation()}
      >
        <Command>
          <CommandInput placeholder="Search members…" className="h-9 text-xs" />
          <CommandList>
            <CommandEmpty>No members found.</CommandEmpty>
            <CommandGroup>
              <CommandItem
                value="Unassigned"
                onSelect={() => void reassign(null)}
                className="text-xs"
              >
                <Check
                  className={cn(
                    "mr-2 size-3.5",
                    !assigneeUserId ? "opacity-100" : "opacity-0",
                  )}
                />
                Unassigned
              </CommandItem>
              {(members ?? []).map((m) => {
                const name = getDisplayName(
                  m.user.firstName,
                  m.user.lastName,
                  m.user.email,
                );
                return (
                  <CommandItem
                    key={m.user._id}
                    value={name}
                    onSelect={() => void reassign(m.user._id)}
                    className="gap-2 text-xs"
                  >
                    <Check
                      className={cn(
                        "size-3.5",
                        assigneeUserId === m.user._id
                          ? "opacity-100"
                          : "opacity-0",
                      )}
                    />
                    <UserAvatar
                      avatarUrl={m.user.avatarUrl}
                      firstName={m.user.firstName}
                      lastName={m.user.lastName}
                      email={m.user.email}
                      className="size-5 text-[9px]"
                    />
                    <span className="truncate">{name}</span>
                  </CommandItem>
                );
              })}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
